import type { Locale } from "@chams-pay/contracts";
import { directionFor } from "@chams-pay/contracts";
import { DirectionProvider } from "@chams-pay/design-system/components/direction";
import { TooltipProvider } from "@chams-pay/design-system/components/tooltip";
import { App as KonstaApp } from "konsta/react";
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { localStore } from "./core/client.js";
import type { WorkspaceScreen } from "./core/navigation.js";
import { useHandheld } from "./core/use-media.js";
import { useSession } from "./core/use-session.js";
import { useStudents } from "./core/use-students.js";
import { useWorkspace } from "./core/use-workspace.js";
import {
  DesktopAccessScreen,
  DesktopInstallationScreen,
  DesktopLockedScreen,
  DesktopOfflineSetupScreen,
} from "./desktop/gates.js";
import { LanguageSwitch } from "./desktop/shell.js";
import { DesktopWorkspace } from "./desktop/workspace.js";
import {
  MobileAccessScreen,
  MobileInstallationScreen,
  MobileLockedScreen,
  MobileOfflineSetupScreen,
} from "./mobile/gates.js";
import { MobileInstallPrompt } from "./mobile/install-prompt.js";
import { installAppHistory } from "./mobile/app-history.js";
import { MobileWorkspace } from "./mobile/workspace.js";

const DevelopmentTools = import.meta.env.DEV
  ? lazy(() =>
      import("./DevelopmentTools.js").then((module) => ({ default: module.DevelopmentTools })),
    )
  : null;

function BootScreen() {
  return (
    <div className="boot-screen fixed inset-0 z-[100] grid min-h-dvh place-items-center bg-primary">
      <span aria-hidden="true" className="chams-logo size-20 bg-white" />
    </div>
  );
}

/**
 * Mounted only once a replica is unlocked. Workspace state (queue, readiness,
 * sync, student search) is created here so both presentations share one instance.
 */
function Workspace({
  session,
  handheld,
}: {
  readonly session: ReturnType<typeof useSession>;
  readonly handheld: boolean;
}) {
  const [screen, setScreen] = useState<WorkspaceScreen>("dashboard");
  const replica = session.replica!;
  const activeUser = session.user ?? replica.authorizedActor ?? null;
  const serverConnected =
    session.online &&
    session.serverSession === "ready" &&
    Boolean(session.user) &&
    Boolean(session.csrf);

  const workspace = useWorkspace({
    locale: session.locale,
    user: session.user,
    csrf: session.csrf,
    online: serverConnected,
    initialReplica: replica,
  });
  const students = useStudents({
    online: serverConnected,
    user: session.user,
    replica: workspace.replica,
    queue: workspace.queue,
    synchronizing: workspace.syncing,
  });
  const studentReturnScreen = useRef<WorkspaceScreen>("students");
  const returnFromStudent = useCallback(() => {
    const destination = studentReturnScreen.current;
    studentReturnScreen.current = "students";
    students.clearSelection();
    setScreen(destination);
  }, [students.clearSelection]);
  const openInstrumentStudent = useCallback(
    (studentId: string) => {
      studentReturnScreen.current = "instruments";
      setScreen("students");
      void students.openStudent(studentId);
    },
    [students.openStudent],
  );
  const resetStudentReturn = useCallback(() => {
    studentReturnScreen.current = "students";
  }, []);
  const historyState = useRef({
    handheld,
    screen,
    studentDetail:
      screen === "students" &&
      (Boolean(students.selected) || studentReturnScreen.current !== "students"),
  });
  historyState.current = {
    handheld,
    screen,
    studentDetail:
      screen === "students" &&
      (Boolean(students.selected) || studentReturnScreen.current !== "students"),
  };
  useEffect(
    () =>
      installAppHistory(() => ({
        ...historyState.current,
        returnFromStudent,
        showDashboard: () => setScreen("dashboard"),
      })),
    [returnFromStudent],
  );

  const shared = {
    locale: session.locale,
    user: activeUser,
    csrf: session.csrf,
    online: serverConnected,
    networkOnline: session.online,
    serverSession: session.serverSession,
    screen,
    onScreenChange: setScreen,
    workspace,
    students,
    updateReady: session.updateReady,
    updateBlocked: session.updateBlocked,
    updateActivating: session.updateActivating,
    onActivateUpdate: () => void session.activateUpdate(),
    onLocaleChange: session.toggleLocale,
    onLock: session.lock,
    onLogout: () => void session.logout(),
    onSignInOnline: session.useAnotherAccount,
    onRetryServerSession: () => void session.retryServerSession(),
  } as const;

  return handheld ? (
    <MobileWorkspace
      {...shared}
      onOpenInstrumentStudent={openInstrumentStudent}
      onStudentBack={returnFromStudent}
      onStudentListOpen={resetStudentReturn}
    />
  ) : (
    <DesktopWorkspace
      {...shared}
      onOpenInstrumentStudent={openInstrumentStudent}
      onStudentBack={returnFromStudent}
      onStudentListOpen={resetStudentReturn}
    />
  );
}

function ChamsPayApplication() {
  const session = useSession();
  const handheld = useHandheld();

  useEffect(() => {
    document.documentElement.classList.toggle("mobile-pwa-shell", handheld);
    return () => document.documentElement.classList.remove("mobile-pwa-shell");
  }, [handheld]);

  return (
    <DirectionProvider direction={directionFor(session.locale)}>
      <KonstaApp
        theme="material"
        dark={false}
        safeAreas
        touchRipple={!handheld}
        className={`application-root min-h-dvh bg-canvas font-sans${
          handheld ? " mobile-presentation" : ""
        }`}
      >
        {session.gate !== "workspace" && !handheld && (
          <div className="fixed top-3 end-3 z-50">
            <LanguageSwitch locale={session.locale} onChange={session.toggleLocale} />
          </div>
        )}

        {session.gate === "loading" && <BootScreen />}

        {session.gate === "auth" &&
          (handheld ? (
            <MobileAccessScreen
              locale={session.locale}
              onLocaleChange={session.toggleLocale}
              onAuthenticated={(user, csrf, passkey) =>
                void session.authenticated(user, csrf, passkey)
              }
            />
          ) : (
            <DesktopAccessScreen
              locale={session.locale}
              onAuthenticated={(user, csrf, passkey) =>
                void session.authenticated(user, csrf, passkey)
              }
            />
          ))}

        {session.gate === "installation" &&
          session.user &&
          session.csrf &&
          (handheld ? (
            <MobileInstallationScreen
              locale={session.locale}
              user={session.user}
              csrf={session.csrf}
              onLocaleChange={session.toggleLocale}
              onReady={(csrf) => void session.installationReady(csrf)}
              onLogout={() => void session.logout()}
            />
          ) : (
            <DesktopInstallationScreen
              locale={session.locale}
              user={session.user}
              csrf={session.csrf}
              onReady={(csrf) => void session.installationReady(csrf)}
              onLogout={() => void session.logout()}
            />
          ))}

        {session.gate === "offline-setup" &&
          (handheld ? (
            <MobileOfflineSetupScreen
              locale={session.locale}
              onLocaleChange={session.toggleLocale}
              passkey={session.pendingPasskey}
              onReady={session.replicaReady}
            />
          ) : (
            <DesktopOfflineSetupScreen
              locale={session.locale}
              passkey={session.pendingPasskey}
              onReady={session.replicaReady}
            />
          ))}

        {session.gate === "locked" &&
          (handheld ? (
            <MobileLockedScreen
              locale={session.locale}
              onlineReauthorized={session.onlineReauthorized}
              pendingPasskey={session.pendingPasskey}
              onLocaleChange={session.toggleLocale}
              onUnlocked={session.unlocked}
              onOnlineSignIn={session.useAnotherAccount}
            />
          ) : (
            <DesktopLockedScreen
              locale={session.locale}
              onlineReauthorized={session.onlineReauthorized}
              pendingPasskey={session.pendingPasskey}
              onUnlocked={session.unlocked}
              onOnlineSignIn={session.useAnotherAccount}
            />
          ))}

        {session.gate === "workspace" && session.replica && (
          <Workspace session={session} handheld={handheld} />
        )}

        {handheld && <MobileInstallPrompt />}
      </KonstaApp>
    </DirectionProvider>
  );
}

export function App() {
  const locale: Locale = document.documentElement.lang === "fr" ? "fr" : "ar";

  if (DevelopmentTools && window.location.pathname === "/__dev__/sync")
    return (
      <Suspense fallback={<BootScreen />}>
        <DevelopmentTools locale={locale} store={localStore} />
      </Suspense>
    );

  return (
    <TooltipProvider>
      <ChamsPayApplication />
    </TooltipProvider>
  );
}
